// BL-177 Step 2d (section 9): per-day business hours for a texting number, and "is this instant after hours?".
//
// Rules (the SQL function public.is_after_hours(number_id, at) follows the same ones):
//   - Everything is judged on the LOCAL WALL CLOCK in the number's time zone (the day of week and the time of day
//     the business itself sees). That is what makes daylight-saving nights come out right: on spring-forward night
//     2:30 AM never happens, and on fall-back night 1:30 AM happens twice; both 1:30s are judged as 1:30.
//   - A day is open from `opens_at` (inclusive) to `closes_at` (exclusive).
//   - A close of 11:59 PM means "until the end of the day" (23:59:xx is still open), because the time picker can't say
//     midnight.
//   - A close EARLIER than the open (e.g. 10:00 PM -> 2:00 AM) runs past midnight into the next morning; those early
//     hours belong to the day the shift started on, even if the next day is marked closed.
//   - Open time == close time is not a window (the database refuses to save it), so that day has no hours.
//   - A day with no row, or a row switched to Closed, has no hours of its own.
//   - No hours saved at all -> never "after hours" (we don't tag every text because nothing was set up).
//
// day_of_week follows JavaScript and Postgres `extract(dow ...)`: 0 = Sunday ... 6 = Saturday.

export const DAYS = [
    { dow: 1, name: 'Monday' },
    { dow: 2, name: 'Tuesday' },
    { dow: 3, name: 'Wednesday' },
    { dow: 4, name: 'Thursday' },
    { dow: 5, name: 'Friday' },
    { dow: 6, name: 'Saturday' },
    { dow: 0, name: 'Sunday' },
];

export const DEFAULT_TIME_ZONE = 'America/New_York';
export const END_OF_DAY = '23:59';

// The zones a US/European operator picks from; any saved IANA zone not in this list is still shown (see timeZoneOptions).
export const TIME_ZONES = [
    { value: 'America/New_York', label: 'Eastern (Atlanta)' },
    { value: 'America/Chicago', label: 'Central (Chicago)' },
    { value: 'America/Denver', label: 'Mountain (Denver)' },
    { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
    { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
    { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
    { value: 'Europe/London', label: 'UK (London)' },
    { value: 'Europe/Berlin', label: 'Central Europe (Berlin)' },
    { value: 'America/Mexico_City', label: 'Mexico (Mexico City)' },
];

export function timeZoneOptions(current) {
    if (!current || TIME_ZONES.some((zone) => zone.value === current)) return TIME_ZONES;
    return [{ value: current, label: current }, ...TIME_ZONES];
}

/** '06:00:00' | '6:00' -> '06:00'. Returns '' for anything that isn't a time. */
export function normalizeTime(value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value || '').trim());
    if (!match) return '';
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return '';
    return `${String(hours).padStart(2, '0')}:${match[2]}`;
}

function toMinutes(value) {
    const time = normalizeTime(value);
    if (!time) return null;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
}

/** Rows from org_message_business_hours -> { [dow]: { is_open, opens_at:'HH:MM', closes_at:'HH:MM' } }. */
export function hoursFromRows(rows) {
    const byDay = {};
    (rows || []).forEach((row) => {
        const dow = Number(row.day_of_week);
        if (!Number.isInteger(dow) || dow < 0 || dow > 6) return;
        byDay[dow] = {
            is_open: row.is_open === true,
            opens_at: normalizeTime(row.opens_at),
            closes_at: normalizeTime(row.closes_at),
        };
    });
    return byDay;
}

/** The pre-2d single window (business_start/end on business_days) as per-day hours; used only if no per-day rows exist. */
export function hoursFromLegacySettings(settings) {
    const byDay = {};
    if (!settings) return byDay;
    const days = Array.isArray(settings.business_days) ? settings.business_days : [];
    DAYS.forEach(({ dow }) => {
        byDay[dow] = {
            is_open: days.includes(dow),
            opens_at: normalizeTime(settings.business_start),
            closes_at: normalizeTime(settings.business_end),
        };
    });
    return byDay;
}

const partsCache = new Map();
function formatterFor(timeZone) {
    if (!partsCache.has(timeZone)) {
        partsCache.set(timeZone, new Intl.DateTimeFormat('en-US', {
            timeZone, hourCycle: 'h23', weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }));
    }
    return partsCache.get(timeZone);
}
const WEEKDAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** The local wall clock for an instant in a zone: { dow, minutes (fractional, incl. seconds), date:'YYYY-MM-DD' }. */
export function localClock(instant, timeZone = DEFAULT_TIME_ZONE) {
    const date = instant instanceof Date ? instant : new Date(instant);
    if (Number.isNaN(date.getTime())) throw new Error('A valid date/time is required.');
    let parts;
    try {
        parts = formatterFor(timeZone || DEFAULT_TIME_ZONE).formatToParts(date);
    } catch (error) {
        throw new Error(`Unknown time zone: ${timeZone}`);
    }
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const hour = Number(get('hour')) % 24;
    return {
        dow: WEEKDAY[get('weekday')],
        minutes: hour * 60 + Number(get('minute')) + Number(get('second')) / 60,
        date: `${get('year')}-${get('month')}-${get('day')}`,
    };
}

function dayWindow(day) {
    if (!day || day.is_open !== true) return null;
    const opens = toMinutes(day.opens_at);
    const closesRaw = toMinutes(day.closes_at);
    if (opens === null || closesRaw === null) return null;
    const closes = normalizeTime(day.closes_at) === END_OF_DAY ? 1440 : closesRaw;
    if (closes === opens) return null;
    return { opens, closes, overnight: closes < opens };
}

/** True when `instant` falls outside the saved hours, judged on the local wall clock in `timeZone`. */
export function isAfterHours(hoursByDay, timeZone = DEFAULT_TIME_ZONE, instant = new Date()) {
    const days = hoursByDay || {};
    if (Object.keys(days).length === 0) return false;
    const { dow, minutes } = localClock(instant, timeZone);
    const today = dayWindow(days[dow]);
    if (today) {
        if (today.overnight ? minutes >= today.opens : (minutes >= today.opens && minutes < today.closes)) return false;
    }
    const yesterday = dayWindow(days[(dow + 6) % 7]);
    if (yesterday && yesterday.overnight && minutes < yesterday.closes) return false;
    return true;
}

/** Plain-English check of one row before saving. Returns '' when fine. */
export function dayRowProblem(day) {
    if (!day || day.is_open !== true) return '';
    if (!normalizeTime(day.opens_at) || !normalizeTime(day.closes_at)) return 'Pick both an opening and a closing time.';
    if (normalizeTime(day.opens_at) === normalizeTime(day.closes_at)) return "Opening and closing can't be the same time.";
    return '';
}

/** '06:00' -> '6:00 AM' (for read-only summaries; inputs use the browser's own time picker). */
export function formatClockTime(value) {
    const minutes = toMinutes(value);
    if (minutes === null) return '';
    const hours = Math.floor(minutes / 60);
    const suffix = hours < 12 ? 'AM' : 'PM';
    return `${hours % 12 === 0 ? 12 : hours % 12}:${String(minutes % 60).padStart(2, '0')} ${suffix}`;
}
